"use client"

import * as React from "react"
import { cn } from "@/lib/utils"

interface ToggleGroupContextValue {
  value: string | undefined
  onValueChange: (value: string) => void
}

const ToggleGroupContext = React.createContext<ToggleGroupContextValue | undefined>(undefined)

interface ToggleGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  type: "single" | "multiple"
  value?: string | string[]
  defaultValue?: string | string[]
  onValueChange?: (value: string | string[] | undefined) => void
  disabled?: boolean
}

const ToggleGroup = React.forwardRef<HTMLDivElement, ToggleGroupProps>(
  ({ className, type, value, defaultValue, onValueChange, disabled, ...props }, ref) => {
    const [internalValue, setInternalValue] = React.useState<string | string[] | undefined>(
      defaultValue
    )

    const currentValue = value !== undefined ? value : internalValue

    const handleValueChange = React.useCallback(
      (itemValue: string) => {
        if (disabled) return

        if (type === "single") {
          const newValue = currentValue === itemValue ? undefined : itemValue
          setInternalValue(newValue)
          onValueChange?.(newValue)
        } else {
          const currentArray = Array.isArray(currentValue) ? currentValue : []
          const newArray = currentArray.includes(itemValue)
            ? currentArray.filter(v => v !== itemValue)
            : [...currentArray, itemValue]
          setInternalValue(newArray)
          onValueChange?.(newArray)
        }
      },
      [currentValue, disabled, onValueChange, type]
    )

    return (
      <ToggleGroupContext.Provider
        value={{
          value: type === "single" ? (currentValue as string) : undefined,
          onValueChange: handleValueChange,
        }}
      >
        <div ref={ref} className={cn("flex items-center justify-center gap-1", className)} {...props} />
      </ToggleGroupContext.Provider>
    )
  }
)

ToggleGroup.displayName = "ToggleGroup"

interface ToggleGroupItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string
}

const ToggleGroupItem = React.forwardRef<HTMLButtonElement, ToggleGroupItemProps>(
  ({ className, value, disabled, ...props }, ref) => {
    const context = React.useContext(ToggleGroupContext)
    if (!context) {
      throw new Error("ToggleGroupItem must be used within a ToggleGroup")
    }

    const isSelected = context.value === value

    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
          isSelected && "bg-accent text-accent-foreground",
          className
        )}
        disabled={disabled}
        onClick={() => context.onValueChange(value)}
        {...props}
      />
    )
  }
)

ToggleGroupItem.displayName = "ToggleGroupItem"

export { ToggleGroup, ToggleGroupItem }
